import './styles.css';
import {
  pairingCodeFromUrl,
  renderPairingScreen,
  requiresIosHomeScreen,
} from './pairing.js';
import { activateNotificationsAndPair, hasDeviceToken } from './push.js';
import { getDeviceToken } from './push.js';
import {
  getCall,
  recordContactOpened,
  selectCallDeal,
  submitCallResult,
  updateCallStatus,
  type MobileCall,
  type MobileCallResultSnapshot,
  type SubmitCallResultResponse,
} from './api.js';
import {
  createCallReturnWatcher,
  renderCallScreen,
} from './call-screen.js';
import { renderDealSelectionScreen } from './deal-selection-screen.js';
import { downloadVCard } from './vcard.js';
import {
  loadPendingResult,
  loadTerminalResult,
  retryPendingResult,
  savePendingResult,
  type RetryPendingResult,
} from './pending-result.js';
import {
  PostCallSubmissionError,
  renderPostCallScreen,
  type PostCallScreenController,
} from './post-call-screen.js';

const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('App root not found');

const isStandalone =
  window.matchMedia('(display-mode: standalone)').matches ||
  Boolean((navigator as Navigator & { standalone?: boolean }).standalone);

if (window.location.pathname === '/pair') {
  renderPairingScreen(root, {
    initialCode: pairingCodeFromUrl(new URL(window.location.href)),
    alreadyPaired: hasDeviceToken(),
    requiresHomeScreen: requiresIosHomeScreen(
      navigator.userAgent,
      isStandalone,
    ),
    activate: activateNotificationsAndPair,
  });
} else if (window.location.pathname.startsWith('/call/')) {
  const callId = decodeURIComponent(window.location.pathname.slice('/call/'.length));
  const loading = document.createElement('section');
  loading.className = 'card';
  loading.textContent = 'Cargando llamada…';
  root.append(loading);
  const deviceToken = getDeviceToken();
  if (!deviceToken) {
    loading.textContent = 'Este celular no está vinculado. Solicita un nuevo código.';
  } else {
    let call: MobileCall | null = null;
    let postCall: PostCallScreenController | null = null;
    let retryTimer: number | null = null;

    const syncMessage = (result: RetryPendingResult): string | null => {
      if (result.state === 'expired') {
        return 'La ventana de 24 horas venció. El resultado guardado se eliminó y no se envió.';
      }
      if (result.state === 'terminal') {
        if (result.status === 409) {
          return 'Este resultado entra en conflicto con lo registrado. Revísalo en Bitrix; no se reintentará automáticamente.';
        }
        if (result.status === 410) {
          return 'La ventana de 24 horas venció. El resultado no se envió y no se reintentará automáticamente.';
        }
        if (result.status === 422) {
          return 'Bitrix necesita una revisión manual. No se reintentará automáticamente.';
        }
        return 'Bitrix rechazó este resultado. Revísalo antes de volver a intentar.';
      }
      if (result.state === 'retryable' || result.state === 'waiting') {
        return 'Resultado guardado en este celular. Se enviará automáticamente al recuperar conexión.';
      }
      return null;
    };

    const renderStandaloneStatus = (message: string, success = false) => {
      root.replaceChildren();
      const card = document.createElement('section');
      card.className = 'card';
      const title = document.createElement('h1');
      title.textContent = success ? 'Resultado registrado' : 'Resultado de llamada';
      const status = document.createElement('p');
      status.className = success ? 'status success' : 'status pending';
      status.setAttribute('role', 'status');
      status.textContent = message;
      card.append(title, status);
      root.append(card);
    };

    const hydrateServerResult = (
      controller: PostCallScreenController,
      snapshot: MobileCallResultSnapshot,
    ) => {
      if (snapshot.state === 'processed' && snapshot.response) {
        controller.showResult(snapshot.response);
        return;
      }
      if (snapshot.state === 'manual_review') {
        controller.showMessage(
          'Bitrix necesita una revisión manual. El resultado queda bloqueado para evitar otra decisión.',
          'error',
          true,
        );
        return;
      }
      if (snapshot.state === 'failed' || snapshot.state === 'unresolved') {
        controller.showMessage(
          'El resultado quedó bloqueado y necesita revisión en Bitrix.',
          'error',
          true,
        );
        return;
      }
      controller.showMessage(
        'El resultado ya está en proceso. Se completará automáticamente.',
        'pending',
        true,
      );
    };

    const scheduleRetry = (nextRetryAt: string) => {
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      const delay = Math.max(0, Date.parse(nextRetryAt) - Date.now());
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        void syncPending();
      }, Math.min(delay, 60_000));
    };

    const syncPending = async (notifyScreen = true): Promise<RetryPendingResult> => {
      const result = await retryPendingResult(
        localStorage,
        callId,
        (payload) => submitCallResult(callId, deviceToken, payload),
      );
      if (
        retryTimer !== null
        && (result.state === 'processed'
          || result.state === 'terminal'
          || result.state === 'expired')
      ) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (result.state === 'processed') {
        if (notifyScreen) {
          if (postCall) postCall.showResult(result.response);
          else renderStandaloneStatus('El resultado quedó sincronizado con Bitrix.', true);
        }
      } else {
        const message = syncMessage(result);
        if (message && notifyScreen) {
          const terminal = result.state === 'terminal' || result.state === 'expired';
          if (postCall) {
            postCall.showMessage(
              message,
              terminal ? 'error' : 'pending',
              true,
            );
          }
          else renderStandaloneStatus(message);
        }
        if (result.state === 'retryable' || result.state === 'waiting') {
          scheduleRetry(result.nextRetryAt);
        }
      }
      return result;
    };

    const submitDecision = async (
      payload: Parameters<typeof savePendingResult>[2],
    ): Promise<SubmitCallResultResponse> => {
      savePendingResult(localStorage, callId, payload);
      const result = await syncPending(false);
      if (result.state === 'processed') return result.response;
      throw new PostCallSubmissionError(
        syncMessage(result)
          ?? 'No se pudo guardar el resultado. Intenta nuevamente.',
        result.state === 'retryable' || result.state === 'waiting'
          ? 'pending'
          : 'error',
      );
    };

    let returnWatcher: ReturnType<typeof createCallReturnWatcher> | null = null;
    const showPostCall = () => {
      if (!call || !call.resultEligible || postCall) return;
      returnWatcher?.stop();
      postCall = renderPostCallScreen(root, call, {
        submit: submitDecision,
        openDialer: (url) => {
          window.location.href = url;
        },
        recordContactOpened: () => recordContactOpened(callId, deviceToken),
        downloadContact: downloadVCard,
      });
      if (call.result) {
        hydrateServerResult(postCall, call.result);
        return;
      }
      const storedTerminal = loadTerminalResult(localStorage, callId);
      if (loadPendingResult(localStorage, callId)) {
        postCall.showMessage(
          'Resultado guardado en este celular. Se enviará automáticamente al recuperar conexión.',
          'pending',
          true,
        );
      } else if (storedTerminal) {
        postCall.showMessage(
          syncMessage({
            state: 'terminal',
            status: storedTerminal.status,
            code: storedTerminal.code,
          })!,
          'error',
          true,
        );
      }
      void syncPending();
    };
    returnWatcher = createCallReturnWatcher({ onReturn: showPostCall });

    const renderLoadedCall = (
      nextCall: MobileCall,
      startupResponse?: SubmitCallResultResponse,
    ) => {
      call = nextCall;
      if (call.dealSelection?.state === 'required') {
        renderDealSelectionScreen(root, call, {
          select: (dealId) => selectCallDeal(callId, deviceToken, dealId),
          onSelected: (selectedCall) => renderLoadedCall(selectedCall),
        });
        return;
      }
      if (
        call.resultEligible
        && (
          call.status === 'dialer_opened'
          || call.result !== null
          || loadPendingResult(localStorage, callId)
          || loadTerminalResult(localStorage, callId)
          || startupResponse
        )
      ) {
        showPostCall();
        if (startupResponse) postCall?.showResult(startupResponse);
        return;
      }
      renderCallScreen(root, call, {
        updateStatus: (status) => updateCallStatus(callId, deviceToken, status),
        onDialerOpened: () => returnWatcher?.arm(),
        openDialer: (url) => {
          window.location.href = url;
        },
        recordContactOpened: () => recordContactOpened(callId, deviceToken),
        downloadContact: downloadVCard,
      });
    };

    const retryOnConnection = () => {
      if (loadPendingResult(localStorage, callId)) void syncPending();
    };
    window.addEventListener('online', retryOnConnection);
    window.addEventListener('focus', retryOnConnection);

    const startupHasResult =
      Boolean(loadPendingResult(localStorage, callId))
      || Boolean(loadTerminalResult(localStorage, callId));
    const startupSync = startupHasResult ? syncPending() : Promise.resolve(null);

    void Promise.allSettled([getCall(callId, deviceToken), startupSync])
      .then(([callResult, syncResult]) => {
        if (callResult.status === 'fulfilled') {
          renderLoadedCall(
            callResult.value,
            syncResult.status === 'fulfilled'
                && syncResult.value?.state === 'processed'
              ? syncResult.value.response
              : undefined,
          );
          return;
        }
        if (!startupHasResult) {
          loading.textContent =
            callResult.reason instanceof Error
              && callResult.reason.message.includes('410')
              ? 'Esta solicitud ya venció. Vuelve a Bitrix para generar una nueva.'
              : 'No se pudo abrir esta llamada en este celular.';
        }
      });
  }
} else {
  const card = document.createElement('section');
  card.className = 'card home-card';
  const mark = document.createElement('div');
  mark.className = 'app-mark';
  mark.textContent = '☎';
  const title = document.createElement('h1');
  title.textContent = 'Llamadas Galjosa';
  const status = document.createElement('p');
  status.className = hasDeviceToken() ? 'status success' : 'description';
  status.textContent = hasDeviceToken()
    ? 'Celular vinculado y listo'
    : 'Abre el enlace o escanea el QR que te entrega el administrador para vincular este equipo.';
  card.append(mark, title, status);
  root.append(card);
}

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js');
  });
}
